import React, { useEffect, useCallback, useState, lazy, Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import {
  ZoomIn,
  ZoomOut,
  Maximize,
  Minimize,
  Download,
  ChevronLeft,
  ChevronRight,
  LoaderCircle,
} from 'lucide-react'
import ImageViewer from './ImageViewer'
import ErrorBoundary from './ErrorBoundary'
import type { PreviewFile } from './types'

const PdfViewer = lazy(() => import('./PdfViewer'))
const ExcelViewer = lazy(() => import('./ExcelViewer'))

interface FilePreviewModalProps {
  isOpen: boolean
  onClose: () => void
  files: PreviewFile[]
  currentIndex: number
  onNavigate?: (index: number) => void
  onDownload?: () => void
}

const FilePreviewModal: React.FC<FilePreviewModalProps> = ({
  isOpen,
  onClose,
  files,
  currentIndex,
  onNavigate,
  onDownload: onDownloadOverride,
}) => {
  const { t } = useTranslation()
  const [zoom, setZoom] = useState(1)
  const [fullscreen, setFullscreen] = useState(false)
  const [pdfPageInfo, setPdfPageInfo] = useState<string>('')

  const currentFile = files[currentIndex]
  const hasPrev = currentIndex > 0
  const hasNext = currentIndex < files.length - 1

  const handleZoomIn = useCallback(() => setZoom((z) => Math.min(z + 0.25, 3)), [])
  const handleZoomOut = useCallback(() => setZoom((z) => Math.max(z - 0.25, 0.5)), [])
  const handleReset = useCallback(() => setZoom(1), [])

  const handlePrev = useCallback(() => {
    if (hasPrev && onNavigate) {
      setZoom(1)
      setPdfPageInfo('')
      onNavigate(currentIndex - 1)
    }
  }, [hasPrev, onNavigate, currentIndex])

  const handleNext = useCallback(() => {
    if (hasNext && onNavigate) {
      setZoom(1)
      setPdfPageInfo('')
      onNavigate(currentIndex + 1)
    }
  }, [hasNext, onNavigate, currentIndex])

  const handleDownload = () => {
    if (onDownloadOverride) {
      onDownloadOverride()
      return
    }
    const url = currentFile?.downloadUrl || currentFile?.url
    if (url) window.open(url, '_blank')
  }

  const toggleFullscreen = useCallback(() => setFullscreen((f) => !f), [])

  const handlePdfPageChange = useCallback((current: number, total: number) => {
    setPdfPageInfo(`${t('Common_page')} ${current} ${t('Common_page_of')} ${total}`)
  }, [t])

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault()
          handlePrev()
          break
        case 'ArrowRight':
          e.preventDefault()
          handleNext()
          break
        case 'Escape':
          e.preventDefault()
          onClose()
          break
        case '+':
        case '=':
          e.preventDefault()
          handleZoomIn()
          break
        case '-':
          e.preventDefault()
          handleZoomOut()
          break
        case '0':
          e.preventDefault()
          handleReset()
          break
        case 'f':
          e.preventDefault()
          toggleFullscreen()
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, handlePrev, handleNext, onClose, handleZoomIn, handleZoomOut, handleReset, toggleFullscreen])

  // Reset zoom when modal opens or file changes
  useEffect(() => {
    if (isOpen) {
      setZoom(1)
      setFullscreen(false)
      setPdfPageInfo('')
    }
  }, [isOpen, currentIndex])

  if (!currentFile) return null

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className={fullscreen
          ? "!flex flex-col max-w-none w-screen h-screen rounded-none p-0 gap-0 overflow-hidden"
          : "max-w-5xl w-[95vw] p-0 gap-0 overflow-hidden"
        }
        hideClose
      >
        {/* Header / Toolbar */}
        <div className="flex items-center gap-2 px-4 py-2 border-b bg-background">
          <DialogTitle className="text-sm font-medium truncate max-w-[400px]">
            {currentFile.title}
          </DialogTitle>
          {files.length > 1 && (
            <span className="text-muted-foreground text-sm shrink-0">
              ({currentIndex + 1} / {files.length})
            </span>
          )}
          {pdfPageInfo && (
            <span className="text-muted-foreground text-xs shrink-0 border-l pl-2 ml-1">
              {pdfPageInfo}
            </span>
          )}

          <div className="ml-auto flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleZoomOut}
              disabled={zoom <= 0.5}
              title={`${t('Common_zoom_out')} (-)`}
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReset}
              title={`${t('Common_reset')} (0)`}
              className="text-xs min-w-[3rem]"
            >
              {Math.round(zoom * 100)}%
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleZoomIn}
              disabled={zoom >= 3}
              title={`${t('Common_zoom_in')} (+)`}
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleFullscreen}
              title={`${t('Common_fullscreen')} (F)`}
            >
              {fullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDownload}
              title={t('Common_download')}
            >
              <Download className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="ml-1"
            >
              &times;
            </Button>
          </div>
        </div>

        {/* Content area */}
        <div className={`relative bg-muted/30 overflow-hidden ${fullscreen ? 'flex-1 min-h-0 flex flex-col' : ''}`} style={fullscreen ? undefined : { minHeight: '400px', maxHeight: 'calc(95vh - 60px)' }}>
          {/* Prev/Next navigation buttons - outside scroll area */}
          {files.length > 1 && (
            <>
              <Button
                variant="secondary"
                size="sm"
                className="absolute left-3 top-1/2 -translate-y-1/2 z-10 opacity-80 hover:opacity-100"
                onClick={handlePrev}
                disabled={!hasPrev}
                title={`${t('Common_previous')} (\u2190)`}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="secondary"
                size="sm"
                className="absolute right-3 top-1/2 -translate-y-1/2 z-10 opacity-80 hover:opacity-100"
                onClick={handleNext}
                disabled={!hasNext}
                title={`${t('Common_next')} (\u2192)`}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </>
          )}

          {/* Scrollable file content */}
          <div className={`px-12 py-4 overflow-auto ${fullscreen ? 'flex-1 min-h-0' : 'h-full'}`}>
            <ErrorBoundary key={`${currentIndex}-${currentFile.url}`} fallbackMessage={t('Common_pdf_error')}>
              {currentFile.type === 'image' && (
                <ImageViewer url={currentFile.url} title={currentFile.title} zoom={zoom} />
              )}
              {currentFile.type === 'pdf' && (
                <Suspense
                  fallback={
                    <div className="flex items-center justify-center p-12" style={{ minHeight: '200px' }}>
                      <LoaderCircle className="h-8 w-8 animate-spin text-primary" />
                    </div>
                  }
                >
                  <PdfViewer url={currentFile.url} fileId={currentFile.fileId} zoom={zoom} onPageChange={handlePdfPageChange} />
                </Suspense>
              )}
              {currentFile.type === 'excel' && (
                <Suspense
                  fallback={
                    <div className="flex items-center justify-center p-12" style={{ minHeight: '200px' }}>
                      <LoaderCircle className="h-8 w-8 animate-spin text-primary" />
                    </div>
                  }
                >
                  <ExcelViewer url={currentFile.url} fileId={currentFile.fileId} fileSize={currentFile.fileSize} zoom={zoom} />
                </Suspense>
              )}
            </ErrorBoundary>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default FilePreviewModal
